/**
 * Helpers for manipulating JSON API responses
 * Note: These functions expect response.data (the JSON API part, not the complete Axios response)
 */

/**
 * Extracts relationship data from a JSON API response
 * @param {Object} apiResponseData - The JSON API response (response.data)
 * @param {string} relationshipName - The name of the relationship to extract
 * @returns {Object|Array|null} The relationship object, an array for has_many, or null if not found
 * @example
 * For a single relationship (has_one/belongs_to)
 * const user = getRelationshipData(response.data, 'user')
 * Returns: { id: "1", type: "user", attributes: { name: "John" } }
 *
 * @example
 * For multiple relationships (has_many)
 * const posts = getRelationshipData(response.data, 'posts')
 * Returns: [{ id: "1", type: "post", attributes: {...} }, { id: "2", type: "post", attributes: {...} }]
 */
export const getRelationshipData = (apiResponseData, relationshipName) => {
  // Basic validations
  if (!apiResponseData?.data?.relationships?.[relationshipName]) {
    return null
  }

  if (!apiResponseData.included || apiResponseData.included.length === 0) {
    return null
  }

  const relationshipRef = apiResponseData.data.relationships[relationshipName].data

  // Handle multiple relationships (has_many)
  if (Array.isArray(relationshipRef)) {
    return relationshipRef
      .map((ref) => apiResponseData.included.find((item) => item.type === ref.type && item.id === ref.id))
      .filter(Boolean)
  }

  // Handle single relationships (has_one/belongs_to)
  if (relationshipRef) {
    return apiResponseData.included.find((item) => item.type === relationshipRef.type && item.id === relationshipRef.id)
  }

  return null
}

/**
 * Extracts a specific attribute from a singlerelationship (has_one/belongs_to)
 * @param {Object} apiResponseData - The JSON API response (response.data)
 * @param {string} relationshipName - The name of the relationship
 * @param {string} attributeName - The name of the attribute to extract
 * @returns {any} The attribute value or undefined
 * @example
 * Get a specific attribute from a relationship
 * const firstName = getRelationshipAttribute(response.data, 'user', 'first_name')
 * Returns: "John"
 */
export const getRelationshipAttribute = (apiResponseData, relationshipName, attributeName) => {
  const relationshipData = getRelationshipData(apiResponseData, relationshipName)
  return relationshipData?.attributes?.[attributeName]
}

/**
 * Extracts multiple relationships at once
 * @param {Object} apiResponseData - The JSON API response (response.data)
 * @param {Array<string>} relationshipNames - The names of the relationships to extract
 * @returns {Object} An object with relationships as camelCase keys
 * @example
 * Extract multiple relationships
 * const { user, employees } = getMultipleRelationships(response.data, [
 *   'user',
 *   'employees'
 * ])
 * Returns: { user: {...}, employees: [...]}
 */
export const getMultipleRelationships = (apiResponseData, relationshipNames) => {
  const result = {}

  relationshipNames.forEach((relationshipName) => {
    // Convert snake_case to camelCase for keys
    const camelCaseKey = relationshipName.replace(/_([a-z])/g, (match, letter) => letter.toUpperCase())
    result[camelCaseKey] = getRelationshipData(apiResponseData, relationshipName)
  })

  return result
}

/**
 * Processes a collection of JSON API resources and enriches each with their relationships
 * @param {Object} apiResponseData - The JSON API collection response (e.g., response.data.rg_contracts)
 * @param {Array<string>} relationshipNames - The names of the relationships to extract for each item
 * @returns {Array} Array of enriched resources with their relationships
 * @example
 * Process a collection with relationships
 * const enrichedContracts = processCollectionWithRelationships(response.data.contracts, ['employee'])
 * Returns: [{ ...contract, employee: {...} }, ...]
 */
export const processCollectionWithRelationships = (apiResponseData, relationshipNames) => {
  const collection = apiResponseData?.data || []

  return collection.map((item) => {
    const enrichedItem = { ...item }

    relationshipNames.forEach((relationshipName) => {
      // Create a mock response structure for the helper
      const mockResponse = {
        data: item,
        included: apiResponseData.included || []
      }

      const relationshipData = getRelationshipData(mockResponse, relationshipName)
      enrichedItem[relationshipName] = relationshipData || null
    })

    return enrichedItem
  })
}
